import AdSetController from '@mm/state/controllers/AdSetController';
import { MessengerAdController } from '@mm/state/controllers/MessengerAdController';
import { AdCampaignModel } from '@mm/state/model/AdCampaignModel';
import AdSetModel from '@mm/state/model/AdSetModel';
import { MessengerAdModel } from '@mm/state/model/MessengerAdModel';
import { getErrorJson, LifeCycleThis } from '@mm/utils';
import { ErrorAlert } from '@mm/components';
import { get } from 'lodash';
import React from 'react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import { compose, lifecycle, withHandlers, withStateHandlers } from 'recompose';
import Button from '../../../../../../common/buttons/DeprecatedButton/DeprecatedButton';
import Checkbox from '../../../../../../common/components/Checkbox';
import SelectorWithArrow from '../../../../../../common/components/SelectorWithArrow/SelectorWithArrow';
import Spinner from '../../../../../../common/components/Spinner';
import { SUCCESSFUL_SYNC } from '../../../../../../common/constants/ads';

type OuterProps = {
  adCampaigns: AdCampaignModel[];
  markAdsPublished: () => void;
  ad: MessengerAdModel;
  facebookPageId: number;
  botId: number;
  campaignsError: string;
  adAccountName: string;
  adAccountId: string;
  accountUserId: number;
};

type PropsFromWithHandlers = {
  onAdCampaignChange: ReturnType<typeof onAdCampaignChange>;
  toggleAd: ReturnType<typeof toggleAd>;
  publishChanges: ReturnType<typeof publishChanges>;
  hasSelectedAds: () => boolean;
};

type PropsFromWithState = {
  adSets: AdSetModel[];
  setAdSets: (adSets: AdSetModel[]) => void;
  busy: boolean;
  setBusy: (value: boolean) => void;
  syncStatus: string;
  setSyncStatus: (value: string) => void;
};

type InnerProps = PropsFromWithHandlers & PropsFromWithState;

type Props = InnerProps & OuterProps;

const AutomaticSync = ({
  adCampaigns,
  onAdCampaignChange,
  adSets,
  toggleAd,
  hasSelectedAds,
  busy,
  publishChanges,
  campaignsError,
  syncStatus,
  botId,
  adAccountName,
  adAccountId,
}: Props) => (
  <div className="ad-details-sync__automatic-sync">
    <div className="ad-details-sync__auto--header">
      <div className="ad-details-sync__auto--selector">
        {!campaignsError && (
          <div className="ad-details-sync__auto--ad-account">
            <div>
              <b>{`${adAccountName} (${adAccountId})`}</b>
            </div>
          </div>
        )}
        {adCampaigns && adCampaigns.length > 0 ? (
          <div>
            <SelectorWithArrow
              onChange={campaignId => onAdCampaignChange(campaignId as number)}
              defaultLabel="Select Ad Campaign"
              items={adCampaigns
                .filter(campaign => campaign.id !== undefined)
                .map((adCampaign: AdCampaignModel) => ({
                  id: adCampaign.id || 0,
                  name: adCampaign.name,
                  key: adCampaign.id,
                }))}
            />
          </div>
        ) : (
          <span>
            {campaignsError ||
              'Cannot find ad campaigns with messages objective'}
          </span>
        )}
      </div>
      <div className="ad-details-sync__auto--action">
        {syncStatus && syncStatus !== SUCCESSFUL_SYNC ? (
          <div className="ad-details-sync__status ad-details-sync__failure">
            <ErrorAlert message={syncStatus} />
          </div>
        ) : (
          <div className="ad-details-sync__status ad-details-sync__success">
            {syncStatus}
          </div>
        )}
        {hasSelectedAds() && (
          <Button green onClick={publishChanges}>
            Publish changes
          </Button>
        )}
      </div>
    </div>
    <div>
      <Spinner
        show={busy}
        position="abosolute"
        backgroundColor="none"
        size={30}
      />
      {adSets && adSets.length > 0 && (
        <div className="ad-details-sync__auto--body">
          {adSets.map((adSet, adSetIndex) => (
            <Card className="ad-details-sync__auto--adset" key={adSet.id}>
              <CardHeader>{adSet.name}</CardHeader>
              <CardBody>
                {adSet.ads &&
                  adSet.ads.data &&
                  adSet.ads.data.map((ad, adIndex) => (
                    <Checkbox
                      isChecked={ad.selected || false}
                      key={ad.id}
                      label={ad.name}
                      onToggle={() => toggleAd(adSetIndex, adIndex)}
                    />
                  ))}
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  </div>
);

const onAdCampaignChange =
  ({ setAdSets, setBusy, facebookPageId, adAccountId }: Props) =>
  async (campaignId: number) => {
    setBusy(true);
    const { adSets } = await AdSetController.retrieveAll(
      facebookPageId,
      adAccountId,
      campaignId,
    );
    setBusy(false);

    setAdSets(adSets);
  };

const errorMessage = (json: { errors: string[] }) =>
  get(json.errors, 'base[0]') ||
  get(json.errors, '[0]') ||
  'Changes not Published';

const publishChanges =
  ({
    adSets,
    setBusy,
    ad,
    botId,
    setSyncStatus,
    accountUserId,
    markAdsPublished,
  }: Props) =>
  async () => {
    const selectedAds = adSets
      .map(adSet => (adSet.ads || { data: [] }).data.filter(ad => ad.selected))
      .reduce((accumulator, currentAds) => [...accumulator, ...currentAds]);

    setBusy(true);
    setSyncStatus('');
    try {
      await MessengerAdController.facebookSyncLegacy(
        botId,
        ad.id,
        accountUserId,
        selectedAds,
      );
      markAdsPublished();
      setSyncStatus(SUCCESSFUL_SYNC);
    } catch (e) {
      const json = await getErrorJson(e);
      setSyncStatus(errorMessage(json));
    }

    setBusy(false);
  };

const toggleAd =
  ({ adSets, setAdSets }: Props) =>
  (adSetIndex: number, adIndex: number) => {
    const adSet = adSets[adSetIndex];
    const ad = get(adSet, `ads.data[${adIndex}]`);

    if (ad) {
      const ad = adSet.ads.data[adIndex];

      const nextAd = {
        ...ad,
        selected: !ad.selected,
      };

      const nextAdSet = {
        ...adSet,
        ads: {
          data: [
            ...adSet.ads.data.slice(0, adIndex),
            ...[nextAd],
            ...adSet.ads.data.slice(adIndex + 1),
          ],
        },
      };

      const nextAdSets = [
        ...adSets.slice(0, adSetIndex),
        ...[nextAdSet],
        ...adSets.slice(adSetIndex + 1),
      ];

      setAdSets(nextAdSets);
    }
  };

async function componentDidUpdate(
  this: LifeCycleThis<Props>,
  prevProps: Props,
) {
  const { adAccountId, setAdSets } = this.props;
  if (adAccountId !== prevProps.adAccountId) {
    setAdSets([]);
  }
}

const hasSelectedAds =
  ({ adSets }: Props) =>
  () => {
    if (adSets && adSets.length > 0) {
      const found = adSets.find(adSet =>
        (adSet.ads || { data: [] }).data.find(ad => ad.selected),
      );
      return !!found;
    }
    return false;
  };

const initialState = {
  adSets: [],
  busy: false,
  syncStatus: '',
};

const enhance = compose<InnerProps, OuterProps>(
  withStateHandlers(initialState, {
    setAdSets: () => adSets => ({ adSets }),
    setBusy: () => value => ({ busy: value }),
    setSyncStatus: () => syncStatus => ({ syncStatus }),
  }),
  withHandlers({
    onAdCampaignChange,
    toggleAd,
    publishChanges,
    hasSelectedAds,
  }),
  lifecycle<Props, {}>({ componentDidUpdate }),
);

export default enhance(AutomaticSync);
